import type {
  FeeRuleCreateInput,
  FeeRuleUpdateInput,
  FeeType,
} from "@/modules/fees/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type FeeRuleDTO = {
  id: string;
  scope: "global" | "shop" | "category";
  scopeId: string | null;
  feeType: string;
  feeMode: string;
  value: number;
  currency: string;
  isActive: boolean;
  createdAt: string;
  label: string;
};

export type FeeRulesListResult = {
  data: FeeRuleDTO[];
  meta: { total: number; limit: number; offset: number };
};

export type FeeRulesListParams = OffsetPageQueryDTO & {
  feeType: FeeType;
  q?: string;
};

export const feesApi = {
  async list(params: FeeRulesListParams) {
    const url = new URL(`${config.apiUrl}/fees/rules`);
    url.searchParams.set("feeType", params.feeType);
    url.searchParams.set("limit", String(params.limit ?? 20));
    url.searchParams.set("offset", String(params.offset ?? 0));
    if (params.q) url.searchParams.set("q", params.q);
    if (params.sort) url.searchParams.set("sort", JSON.stringify(params.sort));
    if (params.filters)
      url.searchParams.set("filters", JSON.stringify(params.filters));
    return fetcher.get<FeeRulesListResult>(url.toString());
  },

  async create(input: FeeRuleCreateInput) {
    return fetcher.post<FeeRuleDTO>(`${config.apiUrl}/fees/rules`, input);
  },

  async update(id: string, input: FeeRuleUpdateInput) {
    return fetcher.patch<FeeRuleDTO>(
      `${config.apiUrl}/fees/rules/${id}`,
      input,
    );
  },

  async remove(id: string) {
    return fetcher.delete<{ ok: boolean }>(`${config.apiUrl}/fees/rules/${id}`);
  },

  async lookupCategories(params: {
    q?: string;
    limit?: number;
    skip?: number;
  }) {
    const url = new URL(`${config.apiUrl}/fees/lookup/categories`);
    if (params.q) url.searchParams.set("q", params.q);
    url.searchParams.set("limit", String(params.limit ?? 20));
    url.searchParams.set("skip", String(params.skip ?? 0));
    return fetcher.get<{
      items: Array<{ id: string; name: string }>;
      total: number;
    }>(url.toString());
  },

  async lookupShops(params: { q?: string; limit?: number; skip?: number }) {
    const url = new URL(`${config.apiUrl}/fees/lookup/shops`);
    if (params.q) url.searchParams.set("q", params.q);
    url.searchParams.set("limit", String(params.limit ?? 20));
    url.searchParams.set("skip", String(params.skip ?? 0));
    return fetcher.get<{
      items: Array<{ id: string; name: string }>;
      total: number;
    }>(url.toString());
  },

  async getCategoryById(id: string) {
    const res = await fetcher.get<{
      item: { id: string; name: string } | null;
    }>(`${config.apiUrl}/fees/lookup/categories/${id}`);
    return res.item ?? null;
  },

  async getShopById(id: string) {
    const res = await fetcher.get<{
      item: { id: string; name: string } | null;
    }>(`${config.apiUrl}/fees/lookup/shops/${id}`);
    return res.item ?? null;
  },
};
